import * as React from 'react';
import { Component } from 'react';
import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import { inject } from '../utils';
import { withTheme } from 'styled-components';
import { styled } from '../utils/styled';
import { propStyles } from '../utils/propStyles';
import Tabs, { TabsProps } from './Tabs';
import ChartComponent from './Chart';
import { DataStore, AccessibilityStore, EntityStore } from '../stores';
import HTag from './HTag';
import { ChartStore } from '../stores';
import { Chart } from '../models/configuration/chart/Chart';
import Disclaimers from './Disclaimers';
import { getDisclaimers } from '../utils/disclaimers';
import { Charts as ChartsConfig } from '../models/configuration/components/Charts';
import { camelCase } from '@kurtosys/ksys-app-template/dist/utils/changeCase';
import TabRegion from '../models/components/TabRegion';
import getTabPanelProps from '../utils/getTabPanelProps';
import { Theme } from '../models/Theme';

const ChartsWrapper = styled<any, any>('div', 'chartsWrapper', 'charts')`
    ${propStyles};
`;

ChartsWrapper.defaultProps = {
    marginY: 'lg',
};

interface ChartsProps {
    dataStore?: DataStore;
    accessibilityStore?: AccessibilityStore;
    entityStore?: EntityStore;
    charts?: Array<Chart>;
    theme?: Theme;
}

@inject('dataStore', 'accessibilityStore', 'entityStore')
@observer
class Charts extends Component<ChartsProps, any> {
    @observable selectedIdForChart;
    @observable.ref chartStores: Map<string, ChartStore>;

    constructor(props: ChartsProps) {
        super(props);
        this.getChartStores();
    }

    getChartStores(): void {
        this.chartStores = this.props.entityStore.chartStores;
    }

    @computed
    get groupedCharts() {
        const { dataStore } = this.props;
        // Show grouped charts if the `_expand_all` querystring is not set to true and the config has tagged this as a grouped chart
        // The `_expand_all` query string is used by the snapshots feature to make all the chart visible for the snapshot
        return this.props.charts.filter(
            chart => !dataStore.isExpandAll && chart.grouped
        );
    }

    @computed
    get doGroupedChartsHaveData(): boolean {
        if (this.chartStores) {
            return (
                this.groupedCharts.find(chart => {
                    const chartStore = this.chartStores.get(chart.id);
                    return chartStore && chartStore.displayChart;
                }) !== undefined
            );
        }
        return false;
    }

    @computed
    get selectedChart() {
        return this.groupedCharts.find(chart =>
            this.selectedChartId
                ? chart.id === this.selectedChartId
                : chart.selected
        );
    }

    @computed
    get ungroupedCharts() {
        const { dataStore } = this.props;
        // Show grouped charts if the `_expand_all` querystring is set to true or the config has not tagged this as a grouped chart
        // The `_expand_all` query string is used by the snapshots feature to make all the chart visible for the snapshot
        return this.props.charts.filter(
            chart => dataStore.isExpandAll || !chart.grouped
        );
    }

    @computed
    get doUngroupedChartsHaveData(): boolean {
        if (this.chartStores) {
            return (
                this.ungroupedCharts.find(chart => {
                    const chartStore = this.chartStores.get(chart.id);
                    return chartStore && chartStore.displayChart;
                }) !== undefined
            );
        }
        return false;
    }

    @computed
    get selectedChartId() {
        const firstSelectedGroupedChartWithData = this.groupedCharts
            .filter(chart => chart.selected)
            .find(chart => this.chartStores.get(chart.id)?.displayChart);
        const firstGroupedChartWithData = this.groupedCharts.find(
            chart => this.chartStores.get(chart.id)?.displayChart
        );
        return (
            this.selectedIdForChart ||
            (firstSelectedGroupedChartWithData &&
                firstSelectedGroupedChartWithData.id) ||
            (firstGroupedChartWithData && firstGroupedChartWithData.id)
        );
    }

    @action
    selectChartId = id => {
        this.selectedIdForChart = id;
    };

    doesChartHaveData(chartId: string) {
        const chartStore = this.chartStores.get(chartId);
        if (chartStore) {
            return chartStore.displayChart;
        }
        return false;
    }

    @computed
    get chartsConfig() {
        return this.props.dataStore.config.rowConfig.charts;
    }

    @computed
    get noDataDisclaimers(): Array<string> {
        const { dataStore, entityStore } = this.props;
        const { disclaimers = {} } = entityStore.value;
        const chartsConfig = this.chartsConfig as ChartsConfig;
        if (chartsConfig && chartsConfig.noDataDisclaimerTypes) {
            const disclaimerTypes = chartsConfig.noDataDisclaimerTypes;
            const noDataDisclaimers = getDisclaimers(
                dataStore,
                disclaimerTypes,
                disclaimers,
                entityStore.queryContext
            );
            return noDataDisclaimers;
        }
    }

    @computed
    get tabsProps() {
        const {
            accessibilityStore,
            theme,
            dataStore,
            entityStore,
        } = this.props;
        const { groupedCharts, selectedChart, selectChartId } = this;

        let tabsProps: TabsProps;
        if (this.doGroupedChartsHaveData) {
            tabsProps = {
                region: TabRegion.Chart,
                tabs: groupedCharts.map((chart: Chart) => ({
                    id: chart.id,
                    label: chart.label,
                })),
                filterCallback: (chart: Chart) => {
                    return (
                        !chart.noDataHidden || this.doesChartHaveData(chart.id)
                    );
                },
                isTabSelected: (id: string) => id === selectedChart.id,
                setTabActive: tab => {
                    const entityInfo: any = {};
                    if (entityStore?.value) {
                        entityInfo.relatedEntity = dataStore.getEntityProperty(
                            entityStore.value as any,
                            dataStore.config.identifierProperty
                        );
                    }
                    dataStore.logDataLayer('tab_navigation', {
                        tabRegion: TabRegion.Chart,
                        tabLabel: tab.label,
                        ...entityInfo,
                    });
                    selectChartId(tab.id);
                },
                getMetricContext: tab => `charts-${camelCase(tab.label)}`,
                getAccessibility: tab => ({
                    label: accessibilityStore.chartProperty(tab.id, 'title'),
                    description: accessibilityStore.chartProperty(
                        tab.id,
                        'description'
                    ),
                    ownerPrefix: 'chart',
                }),
                tabThemeOverride: { chartTab: theme.ChartTab },
            };
        }
        return tabsProps;
    }

    @computed
    get chartsHeader() {
        const charts = this.chartsConfig;
        if (charts && !Array.isArray(charts)) {
            return charts.label || '';
        }
        return '';
    }

    render() {
        const { theme } = this.props;
        const {
            groupedCharts,
            ungroupedCharts,
            noDataDisclaimers,
            chartsHeader,
        } = this;

        if (
            !this.chartStores &&
            !this.doGroupedChartsHaveData &&
            !this.doUngroupedChartsHaveData
        ) {
            return null;
        }

        return (
            <ChartsWrapper {...theme.ChartsWrapper}>
                {chartsHeader && (
                    <HTag
                        componentTheme="ChartsHeader"
                        defaultStyleProps={{
                            color: 'body',
                            fontFamily: 'heading',
                            fontWeight: 'bold',
                            fontSize: 'md',
                        }}
                        level={2}
                        text={chartsHeader}
                    />
                )}

                {this.doGroupedChartsHaveData && this.tabsProps && (
                    <Tabs {...this.tabsProps} {...theme.ChartTabs} />
                )}

                {!this.doGroupedChartsHaveData && (
                    <Disclaimers
                        marginX="0"
                        disclaimers={noDataDisclaimers}
                        theme={{
                            ...theme,
                            DisclaimersList: theme.ChartNoDataDisclaimersList,
                            DisclaimerListItem:
                                theme.ChartNoDataDisclaimerListItem,
                            DisclaimerCharacter:
                                theme.ChartNoDataDisclaimerCharacter,
                            DisclaimerContent:
                                theme.ChartNoDataDisclaimerContent,
                            DisclaimerReturnLink:
                                theme.ChartNoDataDisclaimerReturnLink,
                        }}
                    />
                )}

                {groupedCharts.length > 0 &&
                    this.doesChartHaveData(this.selectedChartId) && (
                        <ChartComponent
                            {...getTabPanelProps(
                                this.selectedChartId,
                                TabRegion.Chart
                            )}
                            chartStore={this.chartStores.get(
                                this.selectedChartId
                            )}
                        />
                    )}

                {ungroupedCharts.map(chart => {
                    if (this.doesChartHaveData(chart.id)) {
                        return (
                            <ChartComponent
                                key={chart.id}
                                chartStore={this.chartStores.get(chart.id)}
                                showHeading={true}
                            />
                        );
                    }
                    return;
                })}
            </ChartsWrapper>
        );
    }
}

export default withTheme(Charts) as any;



// WEBPACK FOOTER //
// ./src/Components/Charts.tsx